import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type DragEvent,
    type HTMLAttributes,
    type MouseEvent as ReactMouseEvent,
    type ReactNode
} from "react";
import { ArrowDown, ArrowUp, Download, EllipsisVertical, RotateCcw, Share2, Star, Trash2, Users, type LucideIcon } from "@fg/icons";
import type { DriveItem } from "@/api/driveApi";
import { useMe, type Me } from "@/auth/useMe";
import { PersonAvatar } from "@/components/PersonAvatar";
import { cn } from "@/lib/utils";
import { formatBytes, formatListDate } from "@/lib/format";
import { modifierLabel, nameFromEmail } from "@/lib/people";
import { baseNameLength } from "@/lib/fileTypes";
import { anchorFrom, type MenuAnchor } from "@/components/ui/menu";
import { useDriveActions } from "../DriveUiProvider";
import type { DriveVariant, ViewMode } from "../types";
import type { SelectionApi } from "./useSelection";
import { defaultDirFor, type SortKey, type SortState } from "./sorting";
import { ItemIcon, ItemThumb } from "./ItemIcon";
import { isDriveDrag, isFileDrag, readDriveDrag, setDriveDrag } from "./dnd";

/** Minimal key-event shape shared by React and native events. */
export interface KeyLike {
    key: string;
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
    preventDefault: () => void;
}

export interface ItemBrowserHandle {
    handleKey: (e: KeyLike) => void;
}

export interface ItemBrowserProps {
    items: DriveItem[];
    view: ViewMode;
    variant: DriveVariant;
    selection: SelectionApi;
    sort: SortState;
    onSortChange: (s: SortState) => void;
    renamingId: string | null;
    onRenameStart: (item: DriveItem) => void;
    onRenameCommit: (item: DriveItem, name: string | null) => void;
    onOpen: (item: DriveItem) => void;
    onPreview: (item: DriveItem) => void;
    onDelete: (items: DriveItem[]) => void;
    onContextMenu: (item: DriveItem, anchor: MenuAnchor, keyboard: boolean) => void;
    onMoveDrop: (ids: string[], targetFolderId: string) => void;
    onFilesDrop: (dt: DataTransfer, folderId: string) => void;
    label: string;
}

type ColumnId = "owner" | "modified" | "opened" | "trashed" | "size";

const COLUMNS: Record<DriveVariant, ColumnId[]> = {
    folder: ["owner", "modified", "size"],
    starred: ["owner", "modified", "size"],
    search: ["owner", "modified", "size"],
    recent: ["owner", "opened", "modified", "size"],
    trash: ["trashed", "size"],
    shared: ["owner", "modified", "size"]
};

const COLUMN_META: Record<ColumnId, { label: string; sortKey: SortKey; className: string; render: (i: DriveItem, me: Me) => ReactNode }> = {
    owner: { label: "Owner", sortKey: "owner", className: "hidden w-44 md:flex", render: (i, me) => <OwnerCell item={i} me={me} /> },
    modified: {
        label: "Modified",
        sortKey: "modified",
        className: "hidden w-44 sm:flex",
        render: (i, me) => `${formatListDate(i.updatedAt)}${i.modifiedBy ? ` · ${modifierLabel(i.modifiedBy, me.email)}` : ""}`
    },
    opened: { label: "Last opened", sortKey: "opened", className: "hidden w-32 sm:flex", render: (i) => formatListDate(i.lastAccessedAt) },
    trashed: { label: "Date binned", sortKey: "trashed", className: "hidden w-32 sm:flex", render: (i) => formatListDate(i.trashedAt) },
    size: {
        label: "Size",
        sortKey: "size",
        className: "hidden w-24 justify-end md:flex",
        render: (i) => (i.kind === "Folder" ? "—" : formatBytes(i.sizeBytes))
    }
};

// Recent carries four columns; the extra two appear only on wide screens.
const RECENT_CLASS: Partial<Record<ColumnId, string>> = { owner: "hidden w-44 xl:flex", modified: "hidden w-44 lg:flex" };
const columnClass = (variant: DriveVariant, c: ColumnId) => (variant === "recent" ? RECENT_CLASS[c] : undefined) ?? COLUMN_META[c].className;

/** Avatar + "me", or the owner's name from their email. */
function OwnerCell({ item, me }: { item: DriveItem; me: Me }) {
    const email = item.ownerEmail;
    return (
        <span className="inline-flex min-w-0 items-center gap-2" title={email ?? me.email}>
            {email ? <PersonAvatar email={email} /> : <PersonAvatar email={me.email ?? me.name} name={me.name} mine />}
            <span className="truncate">{email ? nameFromEmail(email) : "me"}</span>
        </span>
    );
}

/**
 * The shared Drive list/grid: Google-Drive selection (click, Ctrl, Shift,
 * empty-space clear), roving focus + arrow keys, Enter/Space/Del/F2/Ctrl+A/
 * Esc/Shift+F10, hover quick actions, ⋯ overflow, right-click, drag-to-move
 * and drop-to-upload onto folders.
 */
export const ItemBrowser = forwardRef<ItemBrowserHandle, ItemBrowserProps>(function ItemBrowser(props, ref) {
    const { items, view, variant, selection } = props;
    const me = useMe();
    const els = useRef(new Map<string, HTMLElement>());
    const lastPointer = useRef<string>("mouse");
    const [dropTarget, setDropTarget] = useState<string | null>(null);

    const register = (id: string) => (el: HTMLElement | null) => {
        if (el) els.current.set(id, el);
        else els.current.delete(id);
    };

    // Move DOM focus when keyboard navigation (or an explicit request) asks.
    useEffect(() => {
        if (!selection.focusRequest || !selection.focusId) return;
        const el = els.current.get(selection.focusId);
        if (!el) return;
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: "nearest" });
    }, [selection.focusRequest, selection.focusId]);

    const selectedItems = () => items.filter((i) => selection.has(i.id));
    const indexOf = (id: string | null) => (id ? items.findIndex((i) => i.id === id) : -1);

    // Grid: find the nearest card in the next/previous visual row (folder
    // chips and file cards have different widths, so no fixed column maths).
    const verticalIndex = (from: number, dir: 1 | -1): number => {
        const fromItem = items[from];
        const fromEl = fromItem ? els.current.get(fromItem.id) : undefined;
        if (!fromEl) return from;
        const r = fromEl.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        let best = -1;
        let bestTop = dir === 1 ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
        let bestDx = Number.POSITIVE_INFINITY;
        items.forEach((it, i) => {
            const el = els.current.get(it.id);
            if (!el) return;
            const b = el.getBoundingClientRect();
            const inNextRow = dir === 1 ? b.top > r.top + 4 : b.top < r.top - 4;
            if (!inNextRow) return;
            const dx = Math.abs(b.left + b.width / 2 - cx);
            const closerRow = dir === 1 ? b.top < bestTop - 4 : b.top > bestTop + 4;
            const sameRow = Math.abs(b.top - bestTop) <= 4;
            if (closerRow || (sameRow && dx < bestDx)) {
                best = i;
                bestTop = b.top;
                bestDx = dx;
            }
        });
        return best >= 0 ? best : from;
    };

    const handleKey = (e: KeyLike) => {
        if (items.length === 0) return;
        const mod = e.ctrlKey || e.metaKey;
        let focusIdx = indexOf(selection.focusId);
        if (focusIdx < 0) {
            const firstSel = items.findIndex((i) => selection.has(i.id));
            focusIdx = firstSel;
        }
        const current = focusIdx >= 0 ? items[focusIdx] : undefined;

        const goTo = (idx: number) => {
            const clamped = Math.max(0, Math.min(items.length - 1, idx));
            const target = items[clamped];
            if (!target) return;
            if (e.shiftKey) selection.extendTo(target.id, mod, { focus: true });
            else if (mod) selection.setFocus(target.id, { focus: true });
            else selection.selectOnly(target.id, { focus: true });
        };

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                goTo(focusIdx < 0 ? 0 : view === "grid" ? verticalIndex(focusIdx, 1) : focusIdx + 1);
                return;
            case "ArrowUp":
                e.preventDefault();
                goTo(focusIdx < 0 ? 0 : view === "grid" ? verticalIndex(focusIdx, -1) : focusIdx - 1);
                return;
            case "ArrowRight":
                if (view !== "grid") return;
                e.preventDefault();
                goTo(focusIdx < 0 ? 0 : focusIdx + 1);
                return;
            case "ArrowLeft":
                if (view !== "grid") return;
                e.preventDefault();
                goTo(focusIdx < 0 ? 0 : focusIdx - 1);
                return;
            case "Home":
                e.preventDefault();
                goTo(0);
                return;
            case "End":
                e.preventDefault();
                goTo(items.length - 1);
                return;
            case "Enter":
                if (!current) return;
                e.preventDefault();
                props.onOpen(current);
                return;
            case " ":
                if (!current) return;
                e.preventDefault();
                if (mod) selection.toggle(current.id);
                else if (current.kind === "File") props.onPreview(current);
                else selection.selectOnly(current.id, { focus: true });
                return;
            case "Delete": {
                const sel = selectedItems();
                if (sel.length === 0) return;
                e.preventDefault();
                props.onDelete(sel);
                return;
            }
            case "F2": {
                if (variant === "trash") return;
                const sel = selectedItems();
                const target = sel.length === 1 ? sel[0] : sel.length === 0 ? current : undefined;
                if (!target) return;
                e.preventDefault();
                props.onRenameStart(target);
                return;
            }
            case "Escape":
                if (selection.count === 0) return;
                e.preventDefault();
                selection.clear();
                return;
            case "ContextMenu":
            case "F10": {
                if (e.key === "F10" && !e.shiftKey) return;
                const target = current ?? selectedItems()[0];
                if (!target) return;
                e.preventDefault();
                if (!selection.has(target.id)) selection.selectOnly(target.id);
                const el = els.current.get(target.id);
                const anchor: MenuAnchor = el ? anchorFrom(el) : { x: 80, y: 120 };
                props.onContextMenu(target, anchor, true);
                return;
            }
            default:
                if (mod && (e.key === "a" || e.key === "A")) {
                    e.preventDefault();
                    selection.selectAll();
                }
        }
    };

    useImperativeHandle(ref, () => ({ handleKey }));

    const onItemClick = (e: ReactMouseEvent, item: DriveItem) => {
        const mod = e.ctrlKey || e.metaKey;
        if (e.shiftKey) selection.extendTo(item.id, mod);
        else if (mod) selection.toggle(item.id);
        else if (lastPointer.current === "touch") {
            // Touch: tap opens; once something is selected (long-press), taps toggle.
            if (selection.count > 0) selection.toggle(item.id);
            else props.onOpen(item);
        } else selection.selectOnly(item.id);
    };

    const onItemContextMenu = (e: ReactMouseEvent, item: DriveItem) => {
        e.preventDefault();
        e.stopPropagation();
        props.onContextMenu(item, { x: e.clientX, y: e.clientY }, false);
    };

    const openMenuFromButton = (e: ReactMouseEvent<HTMLButtonElement>, item: DriveItem) => {
        e.stopPropagation();
        if (!selection.has(item.id)) selection.selectOnly(item.id);
        props.onContextMenu(item, anchorFrom(e.currentTarget, "end"), e.detail === 0);
    };

    const itemProps = (item: DriveItem): HTMLAttributes<HTMLDivElement> & { draggable?: boolean } => {
        const out: HTMLAttributes<HTMLDivElement> & { draggable?: boolean } = {
            onClick: (e) => onItemClick(e, item),
            onDoubleClick: () => props.onOpen(item),
            onContextMenu: (e) => onItemContextMenu(e, item)
        };
        if (variant !== "trash" && props.renamingId !== item.id) {
            out.draggable = true;
            out.onDragStart = (e: DragEvent<HTMLDivElement>) => {
                const inSel = selection.has(item.id);
                const ids = inSel ? selectedItems().map((i) => i.id) : [item.id];
                if (!inSel) selection.selectOnly(item.id);
                setDriveDrag(e, ids, ids.length > 1 ? `${ids.length} items` : item.name);
            };
        }
        if (item.kind === "Folder" && variant !== "trash") {
            out.onDragOver = (e: DragEvent<HTMLDivElement>) => {
                if (!isDriveDrag(e) && !isFileDrag(e)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = isDriveDrag(e) ? "move" : "copy";
                if (dropTarget !== item.id) setDropTarget(item.id);
            };
            out.onDragLeave = (e: DragEvent<HTMLDivElement>) => {
                if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
                setDropTarget((t) => (t === item.id ? null : t));
            };
            out.onDrop = (e: DragEvent<HTMLDivElement>) => {
                setDropTarget(null);
                if (isDriveDrag(e)) {
                    e.preventDefault();
                    e.stopPropagation();
                    const ids = readDriveDrag(e).filter((id) => id !== item.id);
                    if (ids.length > 0) props.onMoveDrop(ids, item.id);
                } else if (isFileDrag(e)) {
                    e.preventDefault();
                    e.stopPropagation();
                    props.onFilesDrop(e.dataTransfer, item.id);
                }
            };
        }
        return out;
    };

    const tabStopId = selection.focusId ?? items[0]?.id ?? null;
    const commonContainer = {
        onKeyDown: (e: React.KeyboardEvent) => handleKey(e),
        onPointerDown: (e: React.PointerEvent) => {
            lastPointer.current = e.pointerType;
        }
    };

    if (view === "list") {
        const cols = COLUMNS[variant];
        return (
            <div
                role="grid"
                aria-label={props.label}
                aria-multiselectable="true"
                aria-rowcount={items.length + 1}
                className="w-full select-none text-[13px]"
                {...commonContainer}
            >
                <div
                    role="row"
                    data-no-clear=""
                    className="sticky top-0 z-10 flex h-9 items-center border-b border-[var(--drive-line)] bg-card px-3 text-xs font-semibold text-[var(--drive-text-2)]"
                >
                    <HeaderCell label="Name" sortKey="name" sort={props.sort} onSortChange={props.onSortChange} className="min-w-0 flex-1 pl-9" />
                    {cols.map((c) => (
                        <HeaderCell
                            key={c}
                            label={COLUMN_META[c].label}
                            sortKey={COLUMN_META[c].sortKey}
                            sort={props.sort}
                            onSortChange={props.onSortChange}
                            className={cn("shrink-0 px-2", columnClass(variant, c))}
                        />
                    ))}
                    <div className="w-9 shrink-0" aria-hidden />
                </div>
                {items.map((item, i) => {
                    const sel = selection.has(item.id);
                    const renaming = props.renamingId === item.id;
                    return (
                        <div
                            key={item.id}
                            ref={register(item.id)}
                            role="row"
                            aria-selected={sel}
                            aria-rowindex={i + 2}
                            data-item-id={item.id}
                            tabIndex={item.id === tabStopId ? 0 : -1}
                            {...itemProps(item)}
                            className={cn(
                                "group relative flex h-10 cursor-default items-center border-b border-[var(--drive-line)] px-3 outline-none transition-colors",
                                "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                                sel ? "bg-[var(--drive-selected)]" : "hover:bg-[var(--drive-hover)]",
                                dropTarget === item.id && "bg-[var(--drive-selected)] ring-2 ring-inset ring-primary"
                            )}
                        >
                            <div role="gridcell" className="flex min-w-0 flex-1 items-center gap-2.5 pr-2">
                                <ItemThumb item={item} size="row" />
                                {renaming ? (
                                    <InlineRename item={item} onDone={(name) => props.onRenameCommit(item, name)} />
                                ) : (
                                    <>
                                        <span className="truncate" title={item.name}>{item.name}</span>
                                        {item.isShared ? (
                                            <span title="Shared" className="inline-flex shrink-0">
                                                <Users className="h-3.5 w-3.5 text-[var(--drive-text-3)]" aria-label="Shared" />
                                            </span>
                                        ) : null}
                                        {item.isFavorite ? <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" aria-label="Starred" /> : null}
                                        <QuickActions item={item} variant={variant} className="ml-auto hidden group-hover:flex group-focus-within:flex" />
                                    </>
                                )}
                            </div>
                            {cols.map((c) => (
                                <div
                                    key={c}
                                    role="gridcell"
                                    className={cn("shrink-0 items-center truncate px-2 tabular-nums text-[var(--drive-text-2)]", columnClass(variant, c))}
                                >
                                    {COLUMN_META[c].render(item, me)}
                                </div>
                            ))}
                            <div role="gridcell" className="flex w-9 shrink-0 justify-end">
                                <MoreButton item={item} onClick={(e) => openMenuFromButton(e, item)} />
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // Grid: folders as compact chips, files as cards with a preview.
    const folders = items.filter((i) => i.kind === "Folder");
    const files = items.filter((i) => i.kind === "File");
    const cardClass = (item: DriveItem, sel: boolean) =>
        cn(
            "group relative cursor-default rounded-lg border border-[var(--drive-line)] bg-card outline-none transition-[background-color,border-color,box-shadow]",
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
            sel ? "border-primary/50 bg-[var(--drive-selected)]" : "hover:shadow-[var(--drive-shadow-8)]",
            dropTarget === item.id && "border-primary bg-[var(--drive-selected)] ring-2 ring-primary"
        );

    return (
        <div role="listbox" aria-label={props.label} aria-multiselectable="true" className="select-none space-y-6 px-4 pt-2" {...commonContainer}>
            {folders.length > 0 ? (
                <div role="group" aria-label="Folders">
                    <div className="mb-2 px-1 text-xs font-semibold text-[var(--drive-text-2)]" aria-hidden>Folders</div>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-3">
                        {folders.map((item) => {
                            const sel = selection.has(item.id);
                            return (
                                <div
                                    key={item.id}
                                    ref={register(item.id)}
                                    role="option"
                                    aria-selected={sel}
                                    data-item-id={item.id}
                                    tabIndex={item.id === tabStopId ? 0 : -1}
                                    {...itemProps(item)}
                                    className={cn(cardClass(item, sel), "flex h-11 items-center gap-2.5 pl-3 pr-1")}
                                >
                                    <ItemThumb item={item} size="row" />
                                    {props.renamingId === item.id ? (
                                        <InlineRename item={item} onDone={(name) => props.onRenameCommit(item, name)} />
                                    ) : (
                                        <span className="min-w-0 flex-1 truncate text-[13px] font-semibold" title={item.name}>{item.name}</span>
                                    )}
                                    {item.isShared ? <Users className="h-3.5 w-3.5 shrink-0 text-[var(--drive-text-3)]" aria-label="Shared" /> : null}
                                    {item.isFavorite ? <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" aria-label="Starred" /> : null}
                                    <MoreButton item={item} onClick={(e) => openMenuFromButton(e, item)} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
            {files.length > 0 ? (
                <div role="group" aria-label="Files">
                    <div className="mb-2 px-1 text-xs font-semibold text-[var(--drive-text-2)]" aria-hidden>Files</div>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(10.5rem,1fr))] gap-3">
                        {files.map((item) => {
                            const sel = selection.has(item.id);
                            return (
                                <div
                                    key={item.id}
                                    ref={register(item.id)}
                                    role="option"
                                    aria-selected={sel}
                                    data-item-id={item.id}
                                    tabIndex={item.id === tabStopId ? 0 : -1}
                                    {...itemProps(item)}
                                    className={cn(cardClass(item, sel), "flex flex-col overflow-hidden")}
                                >
                                    <div className="flex h-11 items-center gap-2 pl-3 pr-1">
                                        <ItemIcon item={item} className="h-5 w-5" />
                                        {props.renamingId === item.id ? (
                                            <InlineRename item={item} onDone={(name) => props.onRenameCommit(item, name)} />
                                        ) : (
                                            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold" title={item.name}>{item.name}</span>
                                        )}
                                        {item.isShared ? <Users className="h-3.5 w-3.5 shrink-0 text-[var(--drive-text-3)]" aria-label="Shared" /> : null}
                                        <MoreButton item={item} onClick={(e) => openMenuFromButton(e, item)} />
                                    </div>
                                    <div className="relative px-2 pb-2">
                                        <ItemThumb item={item} size="card" />
                                        {item.isFavorite ? (
                                            <Star className="absolute bottom-3.5 left-3.5 h-4 w-4 fill-amber-400 text-amber-500 drop-shadow" aria-label="Starred" />
                                        ) : null}
                                        <QuickActions
                                            item={item}
                                            variant={variant}
                                            className="absolute right-3.5 top-1.5 hidden rounded-[4px] bg-card p-0.5 shadow-[var(--drive-shadow-8)] group-hover:flex"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
});

function HeaderCell({
    label,
    sortKey,
    sort,
    onSortChange,
    className
}: {
    label: string;
    sortKey: SortKey;
    sort: SortState;
    onSortChange: (s: SortState) => void;
    className?: string;
}) {
    const active = sort.key === sortKey;
    return (
        <div
            role="columnheader"
            aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
            className={cn("flex items-center", className)}
        >
            <button
                type="button"
                onClick={() => onSortChange(active ? { key: sortKey, dir: sort.dir === "asc" ? "desc" : "asc" } : { key: sortKey, dir: defaultDirFor(sortKey) })}
                className={cn(
                    "-ml-1 inline-flex items-center gap-1 rounded-[4px] px-1.5 py-1 transition-colors hover:bg-[var(--drive-hover)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    active && "text-foreground"
                )}
                title={`Sort by ${label.toLowerCase()}`}
            >
                {label}
                {active ? (
                    sort.dir === "asc" ? <ArrowUp className="h-3.5 w-3.5" aria-hidden /> : <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                ) : null}
            </button>
        </div>
    );
}

function MoreButton({ item, onClick }: { item: DriveItem; onClick: (e: ReactMouseEvent<HTMLButtonElement>) => void }) {
    return (
        <button
            type="button"
            tabIndex={-1}
            aria-label={`More actions for ${item.name}`}
            aria-haspopup="menu"
            title="More actions"
            onClick={onClick}
            onDoubleClick={(e) => e.stopPropagation()}
            className={cn(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-[4px] text-[var(--drive-text-2)] transition-opacity hover:bg-[var(--drive-subtle)] hover:text-foreground",
                "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 group-aria-selected:opacity-100 [@media(hover:none)]:opacity-100"
            )}
        >
            <EllipsisVertical className="h-4 w-4" />
        </button>
    );
}

function QuickActions({ item, variant, className }: { item: DriveItem; variant: DriveVariant; className?: string }) {
    const actions = useDriveActions();
    const btn = (label: string, Icon: LucideIcon, onClick: () => void, iconClass?: string) => (
        <button
            key={label}
            type="button"
            tabIndex={-1}
            aria-label={`${label}: ${item.name}`}
            title={label}
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            onDoubleClick={(e) => e.stopPropagation()}
            className="flex h-7 w-7 items-center justify-center rounded-[4px] text-[var(--drive-text-2)] transition-colors hover:bg-[var(--drive-subtle)] hover:text-foreground"
        >
            <Icon className={cn("h-4 w-4", iconClass)} />
        </button>
    );
    return (
        <div className={cn("shrink-0 items-center gap-0.5 [@media(hover:none)]:!hidden", className)}>
            {variant === "trash" ? (
                <>
                    {btn("Restore", RotateCcw, () => void actions.restore([item]))}
                    {btn("Delete forever", Trash2, () => void actions.deleteForever([item]))}
                </>
            ) : (
                <>
                    {btn("Share", Share2, () => actions.share(item))}
                    {btn("Download", Download, () => void actions.download([item]))}
                    {btn(
                        item.isFavorite ? "Remove from Starred" : "Add to Starred",
                        Star,
                        () => actions.toggleStar([item]),
                        item.isFavorite ? "fill-amber-400 text-amber-500" : undefined
                    )}
                </>
            )}
        </div>
    );
}

function InlineRename({ item, onDone }: { item: DriveItem; onDone: (name: string | null) => void }) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [value, setValue] = useState(item.name);
    const done = useRef(false);

    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(0, item.kind === "File" ? baseNameLength(item.name) : item.name.length);
        // Only on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const finish = (v: string | null) => {
        if (done.current) return;
        done.current = true;
        onDone(v);
    };

    return (
        <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                    e.preventDefault();
                    finish(value);
                } else if (e.key === "Escape") {
                    e.preventDefault();
                    finish(null);
                }
            }}
            onBlur={() => finish(value)}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onDragStart={(e) => e.preventDefault()}
            aria-label={`Rename ${item.name}`}
            className="h-7 min-w-0 flex-1 rounded-[4px] border border-primary bg-background px-2 text-[13px] outline-none ring-2 ring-primary/25"
        />
    );
}
